import { useWeb3React } from '@web3-react/core'
import Big from 'big.js'
import { findByChainId } from 'chain-list'
import { useChainId } from 'metronome-ui/hooks/chains'
import { fromUnit, toUnit } from 'metronome-utils'
import useTranslation from 'next-translate/useTranslation'
import { useContext, useEffect, useMemo, useState } from 'react'
import useSWR from 'swr'

import { useDisabledOperation } from '../hooks/useDisabledOperation'
import { useExecuteTransactions } from '../hooks/useExecuteTransactions'
import useProjectedDebtInUsd from '../hooks/useProjectedDebtInUsd'
import useProjectedIssuableLimitInUsd from '../hooks/useProjectedIssuableLimitInUsd'
import useRefreshedUsdRate from '../hooks/useRefreshedUsdRate'
import { useValidation } from '../hooks/useValidation'

import BorrowSynth from './BorrowSynth'
import FreezeProps from './FreezeProps'
import Loop, { DEFAULT_LOOP } from './Loop'
import { MetronomeContext } from './MetronomeContext'
import PositionSummary from './PositionSummary'
import Routing from './Routing'
import Slippage, {
  CC_SLIPPAGE_DEFAULT,
  CC_SLIPPAGE_OPTIONS,
  SLIPPAGE_DEFAULT,
  SLIPPAGE_OPTIONS
} from './Slippage'
import StatusMessage from './StatusMessage'
import SupplyCollateral from './SupplyCollateral'
import { OperationDetailsPreviewPosition } from './SynthOperationDetails'
import TransactionContext from './TransactionContext'

// eslint-disable-next-line complexity
function CreatePosition({ onSubmit }) {
  const { t } = useTranslation()
  const chainId = useChainId()
  const { decimals: nativeTokenDecimals } = findByChainId(chainId)
  const { account } = useWeb3React()
  const {
    collateralBalances,
    debtPosition: { debtInUsd = 0, issuableLimitInUsd = 0 } = {},
    selectedPoolData: {
      collaterals,
      synthetics,
      status: { isBridgingActive } = {},
      swapRoutes
    } = {},
    selectedToken: selectedCollateral,
    selectedTokenUsdRate: selectedCollateralUsdRate,
    inputValue: collateralInputValue,
    operationExecuted,
    metronome,
    setInputValue,
    setOperationExecuted
  } = useContext(MetronomeContext)
  const { executeTransactions } = useExecuteTransactions()
  const {
    currentTransactions,
    options: transactionOptions,
    openedInternalTransactionId
  } = useContext(TransactionContext)
  const [crossChainDstId, setCrossChainDstId] = useState(chainId)
  const crossChainEnabled = useMemo(
    () =>
      crossChainDstId &&
      crossChainDstId !== chainId &&
      selectedCollateral &&
      selectedCollateral.bridgeToken &&
      isBridgingActive &&
      process.env.NEXT_PUBLIC_FLAGS_CROSS_CHAIN === 'true',
    [chainId, crossChainDstId, isBridgingActive, selectedCollateral]
  )
  const defaultSlippage = crossChainEnabled
    ? CC_SLIPPAGE_DEFAULT
    : SLIPPAGE_DEFAULT
  const [selectedSynthetic, setSelectedSynthetic] = useState()
  const [leverage, setLeverage] = useState(DEFAULT_LOOP)
  const [slippage, setSlippage] = useState(defaultSlippage)
  const [borrowed, setBorrowed] = useState(0)
  const [enableBorrow, setEnableBorrow] = useState(0)
  const [trackCcOperation, setTrackCcOperation] = useState(true)

  const [selectedSynthUsdRate] = useRefreshedUsdRate({
    metronome,
    token: selectedSynthetic
  })

  const { assetIsInactive, isStopped, noSupply } = useDisabledOperation({
    asset: selectedCollateral,
    operation: 'leverage'
  })

  const { assetIsInactive: syntheticIsInactive, noSupply: noSyntheticSupply } =
    useDisabledOperation({
      asset: selectedSynthetic,
      operation: 'leverage'
    })

  const projectedIssuableLimitInUsd = useProjectedIssuableLimitInUsd(
    selectedCollateralUsdRate,
    collateralInputValue && Big(leverage).times(collateralInputValue),
    'deposit'
  )

  const projectedDebtInUsd = useProjectedDebtInUsd({
    inputValue: borrowed,
    operation: 'generate',
    usdRate: selectedSynthUsdRate
  })

  const estimateApy = (collateralApy, syntheticInterestRate, loopFactor) =>
    collateralApy
      ? Big(collateralApy)
          .minus(syntheticInterestRate)
          .times(loopFactor - 1)
          .plus(collateralApy)
          .toNumber()
      : 0

  const estimatedAPY = useMemo(
    function () {
      if (
        selectedCollateral?.apy &&
        selectedSynthetic?.interestRate &&
        Big(leverage).gt(0)
      ) {
        return estimateApy(
          selectedCollateral.apy,
          selectedSynthetic.interestRate,
          leverage
        )
      }
      return null
    },
    [leverage, selectedCollateral?.apy, selectedSynthetic?.interestRate]
  )

  const calculateFeesFetcher = () =>
    fetch(
      `/api/cc-calculate-fees/${account}?srcChainId=${chainId}&dstChainId=${crossChainDstId}&operation=leverage&syntheticToken=${selectedSynthetic?.address}`
    ).then(result => result.json())

  const {
    data: { nativeFee: displayedNativeFee = 0 } = {},
    isValidating: validatingNativeFee
  } = useSWR(
    account && chainId && crossChainEnabled && selectedSynthetic
      ? `cc-calculate-fees-${account}-${chainId}-${crossChainDstId}-${selectedSynthetic?.address}`
      : null,
    calculateFeesFetcher,
    {
      refreshInterval: parseInt(process.env.NEXT_PUBLIC_REFRESH_INTERVAL) * 1000
    }
  )

  const handleSubmit = function () {
    onSubmit?.()
    setOperationExecuted(true)
    const _transactionOptions = {
      useEip1559: transactionOptions.useEip1559
    }

    const operationSubject = selectedCollateral.symbol
    const analyticsId = `Leverage ${selectedCollateral.symbol}`

    const transactionExecutionOptions = {
      analytics: {
        events: {
          'error': `Leverage failed for ${analyticsId}`,
          'error-timeout': `Leverage timeout in wallet for ${analyticsId}`,
          'result': `Leverage succeeded for ${analyticsId}`,
          'transactions': `Leverage initiated for ${analyticsId}`
        },
        toast: {
          close: `Closing pending tx notification for Leverage in ${analyticsId}`,
          reopen: `Reopening Tx Modal for Leverage in ${analyticsId}`
        },
        useEip1559: `Using EIP-1559 to Leverage ${selectedCollateral.symbol}`
      },
      events: {
        extendedStatus: {
          borrowed,
          borrowedInUsd: Big(borrowed).times(selectedSynthUsdRate).toNumber(),
          collateralDeposited: Big(collateralInputValue).toNumber(),
          collateralDepositedInUsd: Big(collateralInputValue)
            .times(selectedCollateralUsdRate)
            .toNumber(),
          collateralSymbol: selectedCollateral.symbol,
          estimatedAPY,
          leverage,
          operation: 'leverage',
          operationSubject,
          synthSymbol: selectedSynthetic.symbol
        }
      },
      onError() {
        setOperationExecuted(false)
      },
      onSuccess() {
        setOperationExecuted(false)
        setTrackCcOperation(true)
        setInputValue(0)
      }
    }

    if (crossChainEnabled) {
      return calculateFeesFetcher().then(function ({ nativeFee, lzArgs }) {
        const { emitter } = metronome.crossChainLeverage({
          _leverage: leverage,
          amount: toUnit(collateralInputValue, selectedCollateral?.decimals),
          bridgeToken: selectedCollateral?.bridgeToken,
          collateralToken: selectedCollateral?.address,
          depositToken: selectedCollateral?.depositToken,
          lzArgs,
          mainCollateral: selectedCollateral?.mainCollateral?.address,
          nativeFee,
          slippage,
          syntheticToken: selectedSynthetic?.address,
          transactionOptions: _transactionOptions
        })
        transactionExecutionOptions.events.extendedStatus.transactions.nativeFee =
          fromUnit(displayedNativeFee, nativeTokenDecimals)
        transactionExecutionOptions.events.extendedStatus.transactions.dstChainId =
          crossChainDstId
        executeTransactions(emitter, transactionExecutionOptions)
      })
    }
    const { emitter } = metronome.leverage({
      _leverage: leverage,
      amount: toUnit(collateralInputValue, selectedCollateral?.decimals),
      collateralToken: selectedCollateral?.address,
      depositToken: selectedCollateral?.depositToken,
      mainCollateral: selectedCollateral?.mainCollateral?.address,
      slippage,
      syntheticToken: selectedSynthetic?.address,
      transactionOptions: _transactionOptions
    })
    executeTransactions(emitter, transactionExecutionOptions)
  }

  const getLeverageValues = function (collateralFactor = 50, { dp = 2 } = {}) {
    const min = 1
    const max = Big(100)
      .div(Big(100).minus(collateralFactor))
      .times(0.95)
      .round(dp, Big.roundDown)
      .toNumber()
    const marks = {}
    for (let i = min; i <= max; i++) {
      marks[i] = {
        label: <span className="text-white">{i}x</span>,
        style: { marginTop: '2px' }
      }
    }
    return {
      marks,
      max,
      min
    }
  }

  const availableCollaterals = useMemo(
    () =>
      swapRoutes.leverageRoutes.map(function (route) {
        const collateral = collaterals.find(c => c.address === route.collateral)
        const { max: maxLeverage } = getLeverageValues(
          collateral.collateralFactor
        )
        const collateralSyntheticsInterestRates = synthetics
          .filter(synthetic => route.synth === synthetic.address)
          .map(({ interestRate }) => interestRate)

        const minInterestRate = Math.min(...collateralSyntheticsInterestRates)
        const maxLeveragedApy = Big(
          estimateApy(collateral.apy, minInterestRate, maxLeverage).toFixed(2)
        )

        return {
          ...collateral,
          ...route.tokenIn, // this overrides some properties of `collateral` above
          estimatedApy: {
            apy: collateral.apy,
            maxLeveragedApy,
            text: t('estimated-apy')
          },
          mainCollateral: {
            address: collateral.address,
            decimals: collateral.decimals,
            symbol: collateral.symbol
          },
          ...(route.tokenIn.address !== route.collateral
            ? {
                message: t('token-in-message', {
                  collateral: collateral.symbol,
                  tokenIn: route.tokenIn.symbol
                })
              }
            : {})
        }
      }),
    [collaterals, swapRoutes.leverageRoutes, synthetics, t]
  )

  const leveragedAmount = useMemo(
    function () {
      if (selectedCollateral && leverage) {
        return Big(selectedCollateral ? collateralInputValue : 0).mul(leverage)
      }
      return 0
    },
    [collateralInputValue, leverage, selectedCollateral]
  )

  const unitLeveragedAmount = useMemo(
    function () {
      if (selectedCollateral && leveragedAmount) {
        return toUnit(leveragedAmount, selectedCollateral.decimals)
      }
      return 0
    },
    [leveragedAmount, selectedCollateral]
  )

  const unitBorrowed = useMemo(
    function () {
      if (borrowed && selectedSynthetic) {
        return toUnit(borrowed, selectedSynthetic.decimals)
      }
      return 0
    },
    [borrowed, selectedSynthetic]
  )

  const {
    availableSupply: availableSynthSupply = 0,
    debtAvailableSupply: availableDebtSupply = 0
  } = selectedSynthetic || {}
  const { availableSupply: availableCollateralSupply = 0 } =
    selectedCollateral || {}

  const collateralTotalSupplyExceeded = Big(unitLeveragedAmount).gt(
    availableCollateralSupply
  )

  const syntheticTotalSupplyExceeded =
    Big(unitBorrowed).gt(availableSynthSupply)

  const debtTokenTotalSupplyExceeded = Big(unitBorrowed).gt(availableDebtSupply)

  const userAssetBalance =
    selectedCollateral && collateralBalances[selectedCollateral.address]
      ? collateralBalances[selectedCollateral.address]?.balance
      : 0

  const userBalance = Big(availableCollateralSupply).gt(userAssetBalance)
    ? fromUnit(userAssetBalance, selectedCollateral?.decimals)
    : fromUnit(
        availableCollateralSupply,
        selectedCollateral?.mainCollateral?.decimals ||
          selectedCollateral?.decimals
      )

  const { amountIsZero, notEnoughBalance, walletIsActive } = useValidation({
    balance: userBalance
  })

  useEffect(
    function () {
      if (leverage > 1 && collateralInputValue > 0) {
        setEnableBorrow(true)
      } else {
        setEnableBorrow(false)
      }
    },
    [collateralInputValue, leverage]
  )

  useEffect(
    function () {
      const openedTransaction = currentTransactions.find(
        transaction =>
          transaction.internalTransactionId === openedInternalTransactionId &&
          transaction.operation === 'leverage'
      )

      const leverageTxHash =
        openedTransaction?.suffixes.indexOf('crossChainLeverage') === -1
          ? undefined
          : openedTransaction?.[
              `transactionHash-${openedTransaction?.suffixes.indexOf(
                'crossChainLeverage'
              )}`
            ]

      if (
        account &&
        chainId &&
        crossChainEnabled &&
        leverageTxHash &&
        trackCcOperation
      ) {
        fetch(`/api/cc-wallet-transactions/${account}`, {
          body: JSON.stringify({
            chainId,
            transactionId: leverageTxHash
          }),
          headers: { 'Content-Type': 'application/json' },
          method: 'POST'
        })
          .then(() => setTrackCcOperation(false))
          .catch(() => setTrackCcOperation(true))
      }
    },
    [
      account,
      chainId,
      crossChainEnabled,
      currentTransactions,
      openedInternalTransactionId,
      trackCcOperation
    ]
  )

  return (
    <form
      onSubmit={function (e) {
        e.preventDefault()
        return handleSubmit()
      }}
    >
      <div className="sm:space-x-6 md:flex">
        <div className="bg-block w-full space-y-4 rounded-md p-6 text-white md:w-1/2">
          <div>
            <StatusMessage
              asset={selectedCollateral?.mainCollateral}
              operation="smartfarming"
              showAssetMessage={selectedCollateral && assetIsInactive}
              showMessage={isStopped || (selectedCollateral && assetIsInactive)}
              showStatusMessage={isStopped}
            />
            <StatusMessage
              asset={selectedSynthetic}
              operation="smartfarming"
              showAssetMessage={selectedSynthetic && syntheticIsInactive}
              showMessage={selectedSynthetic && syntheticIsInactive}
              showStatusMessage={false}
            />
          </div>
          <SupplyCollateral
            amountIsZero={amountIsZero}
            assetIsInactive={assetIsInactive}
            availableCollaterals={availableCollaterals}
            isStopped={isStopped}
            noSupply={noSupply}
            noSyntheticSupply={noSyntheticSupply}
            notEnoughBalance={notEnoughBalance}
            syntheticIsInactive={syntheticIsInactive}
            userBalance={userBalance}
          />
          <Loop
            {...getLeverageValues(selectedCollateral?.collateralFactor)}
            disabled={!(collateralInputValue > 0)}
            onChange={setLeverage}
          />
          <div
            className={`bg-blue-secondary rounded-lg p-4 ${
              enableBorrow ? '' : 'opacity-20'
            }`}
          >
            <BorrowSynth
              availableDebtSupply={fromUnit(
                availableDebtSupply,
                selectedSynthetic?.decimals
              )}
              availableSynthSupply={fromUnit(
                availableSynthSupply,
                selectedSynthetic?.decimals
              )}
              borrowed={borrowed}
              debtTokenTotalSupplyExceeded={debtTokenTotalSupplyExceeded}
              disabled={!enableBorrow}
              leverage={leverage}
              onChange={setSelectedSynthetic}
              selectedSynthetic={selectedSynthetic}
              setBorrowed={setBorrowed}
              syntheticTotalSupplyExceeded={syntheticTotalSupplyExceeded}
            />
            {selectedCollateral?.bridgeToken && (
              <Routing
                crossChainDstId={crossChainDstId}
                isCrossChain={crossChainEnabled}
                setCrossChainDstId={setCrossChainDstId}
                setSlippage={setSlippage}
              />
            )}
            <Slippage
              defaultValue={defaultSlippage}
              disabled={!enableBorrow}
              onChange={setSlippage}
              options={
                crossChainEnabled ? CC_SLIPPAGE_OPTIONS : SLIPPAGE_OPTIONS
              }
              value={slippage}
            />
            <FreezeProps
              Component={OperationDetailsPreviewPosition}
              className="bg-blue-5 mt-6"
              debtInUsd={debtInUsd}
              freeze={operationExecuted}
              issuableLimitInUsd={issuableLimitInUsd}
              projectedDebtInUsd={projectedDebtInUsd}
              projectedIssuableLimitInUsd={projectedIssuableLimitInUsd}
              showHealthFactor
            />
          </div>
        </div>
        <PositionSummary
          amountIsZero={amountIsZero}
          borrowed={borrowed}
          collateralTotalSupplyExceeded={collateralTotalSupplyExceeded}
          crossChainEnabled={crossChainEnabled}
          debtTokenTotalSupplyExceeded={debtTokenTotalSupplyExceeded}
          displayedNativeFee={displayedNativeFee}
          estimatedAPY={estimatedAPY}
          leverage={leverage}
          leveragedAmount={leveragedAmount}
          operationExecuted={operationExecuted}
          selectedSynthetic={selectedSynthetic}
          syntheticTotalSupplyExceeded={syntheticTotalSupplyExceeded}
          validatingNativeFee={validatingNativeFee}
          walletIsActive={walletIsActive}
        />
      </div>
    </form>
  )
}

export default CreatePosition
